import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, map, mergeMap, of, takeUntil } from 'rxjs';
import { clearAllState } from '../../../core/store/actions/app.actions';
import { patientSwitchInitiated } from '../../../user/store/actions/user.actions';
import { selectPatientId } from '../../../user/store/selectors/user.selectors';
import { PatientGoalsService } from '../../services/patient-goals.service';
import * as actions from '../actions/patient-goals.actions';

@Injectable()
export class PatientGoalsEffects {
  private actions$ = inject(Actions);
  private patientGoalsService = inject(PatientGoalsService);
  private store = inject(Store);
  private readonly cancelRequest$ = this.actions$.pipe(ofType(patientSwitchInitiated, clearAllState));

  getPatientGoals$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.getPatientGoals),
      concatLatestFrom(() => [this.store.select(selectPatientId)]),
      mergeMap(([, patientId]) => {
        return this.patientGoalsService.getPatientGoals(patientId).pipe(
          takeUntil(this.cancelRequest$),
          map(goals => actions.getPatientGoalsSuccess({ goals })),
          catchError(err => of(actions.getPatientGoalsFailure({ error: err.error?.errors })))
        );
      })
    );
  });

  getPatientGoalById$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.getPatientGoalById),
      concatLatestFrom(() => [this.store.select(selectPatientId)]),
      mergeMap(([action, patientId]) => {
        return this.patientGoalsService.getPatientGoalById(action.goalId, patientId).pipe(
          takeUntil(this.cancelRequest$),
          map(goal => actions.getPatientGoalByIdSuccess({ goal })),
          catchError(err => of(actions.getPatientGoalByIdFailure({ error: err.error?.errors })))
        );
      })
    );
  });

  recordPrintEvent$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.recordPatientGoalPrintEvent),
      mergeMap(({ patientId, patientGoalId }) =>
        this.patientGoalsService.postPrintEvent(patientId, patientGoalId).pipe(
          map(() => actions.recordPatientGoalPrintEventSuccess()),
          catchError(err => of(actions.recordPatientGoalPrintEventFailure({ error: err })))
        )
      )
    );
  });
}
